<template>
  <app v-if="isReady" />
  <c-splash-screen v-else />
</template>

<script setup>
import App from './app.vue';
import CSplashScreen from './onify/citizen/components/c-splash-screen.vue';
import { router } from './router';
import {
  BOOT_TEST_GLOBALS,
  applyBootstrappedPublicStrings,
  applyLegacyCustomerStrings,
  resolveActiveThemePackName,
  resolveClientCodeDefaultTheme,
  loadAllTranslations,
  applyPublicStrings,
  useOUserSettingsStore,
  hSetValidatorsLocale,
  useHThemePack,
  useHLocale,
  useOSettings,
  resolveActiveAppClientCode,
  resolveActiveAppTheme,
  resolveActiveDefaultTheme,
  isPublicAuthPath,
  settingsLoadPromise,
  authenticatedSettingsLoadPromise,
} from '@onify/helix-core';
import { reconcileRouterWithBrowserUrl, useModuleRoutes, moduleRoutesReadyPromise } from '@onify/helix-core/citizen-runtime';
import { storeToRefs } from 'pinia';
import { useAsyncState } from '@vueuse/core';
import { generatedTranslations } from '../.helix/strings';
// Make sure everything is loaded before we render the app

// Load user settings
const { oUserSettings } = storeToRefs(useOUserSettingsStore());

// Initial settings bootstrap may run before auth; refetch once the user is known.
useSettingsLoadWhenAuthenticated();
const { hSetThemePack, hThemePacksDict } = useHThemePack();
const { hSetLocale } = useHLocale();
const { user_settings_select_theme, custom_title } = useOSettings();

watch(
  () => custom_title?.value,
  (raw) => {
    if (!isPublicAuthPath()) {
      return;
    }

    const label = String(raw ?? 'Onify - ').replace(/[\s-]+$/, '');
    if (label) {
      document.title = label;
    }
  },
  { immediate: true },
);

async function waitForAuthenticatedConfig() {
  if (isPublicAuthPath()) {
    return;
  }

  await Promise.all([settingsLoadPromise, authenticatedSettingsLoadPromise]);
}

// Load all translations from Cache, then overlay legacy per-customer strings
// (backward compatibility only — resolved from apiSettings.app_client_code,
// read directly off the API-driven state rather than the reactive-merged ref,
// since the merge watcher flushes asynchronously and app_client_code is never
// guessed at boot), then finally overlay anonymous/authenticated public
// strings so the API always has the final word.
const { isReady: isTranslationsLoaded } = useAsyncState(async () => {
  await loadAllTranslations(generatedTranslations);
  await waitForAuthenticatedConfig();
  applyLegacyCustomerStrings(resolveActiveAppClientCode());
  applyBootstrappedPublicStrings(applyPublicStrings);
});
const { isReady: isSettingsLoaded } = useAsyncState(waitForAuthenticatedConfig);

const moduleRoutesReady = ref(false);
moduleRoutesReadyPromise.finally(() => {
  moduleRoutesReady.value = true;
});

// Theme precedence: Hub app_theme, then Hub default_theme, then bundled client-code
// overlay; per-user preference overrides when enabled. Public bootstrap is used
// until authenticated settings arrive — merged YAML default_theme is the generic
// build default and must not hide Hub karlskrona-light-new.
watch(
  [
    () => resolveActiveAppTheme(),
    () => resolveActiveDefaultTheme(),
    () => resolveActiveAppClientCode(),
    () => oUserSettings.value?.settings?.theme,
    () => user_settings_select_theme?.value,
  ],
  () => {
    const { theme, unknownAppTheme } = resolveActiveThemePackName({
      appTheme: resolveActiveAppTheme(),
      defaultTheme: resolveActiveDefaultTheme(),
      clientCodeDefaultTheme: resolveClientCodeDefaultTheme(resolveActiveAppClientCode()),
      userTheme: oUserSettings.value?.settings?.theme,
      userThemeSelectionEnabled: user_settings_select_theme?.value ?? false,
      knownThemePacks: hThemePacksDict,
    });

    if (unknownAppTheme) {
      console.warn(`[settings] app_theme "${unknownAppTheme}" is not a known theme pack; ignoring.`);
      return;
    }

    if (theme) {
      hSetThemePack(theme);
    }
  },
  { immediate: true },
);

watch(
  () => oUserSettings.value?.settings?.locale,
  (value) => {
    // Load authenticated strings for every user. user_settings_select_language only
    // controls whether the language *picker* is shown — not whether strings are fetched.
    if (value) {
      hSetLocale(value);
      hSetValidatorsLocale(value);
    }
  },
);

const isReady = computed(() => {
  if (!isTranslationsLoaded.value || !isSettingsLoaded.value || !moduleRoutesReady.value) {
    return false;
  }

  if (isPublicAuthPath()) {
    return true;
  }

  return !!oUserSettings.value?.key;
});

// Sync API-driven module routes while the splash screen is visible.
useModuleRoutes(router);

async function waitForModuleRoutesReady() {
  await moduleRoutesReadyPromise;
}

watch(
  isReady,
  async (ready) => {
    if (!ready) {
      return;
    }

    await router.isReady();
    await waitForModuleRoutesReady();
    await reconcileRouterWithBrowserUrl(router);
  },
  { immediate: true },
);

if (import.meta.env.MODE === 'test') {
  window[BOOT_TEST_GLOBALS.APP_BOOT_READY] = isReady.value;

  watch(
    isReady,
    (ready) => {
      window[BOOT_TEST_GLOBALS.APP_BOOT_READY] = ready;
    },
    { immediate: true },
  );
}

onMounted(() => {
  // Static splash in index.html covers API bootstrap before Vue mounts.
  document.getElementById('boot-splash')?.remove();
});
</script>

<style lang="scss">
@import './custom/styles/**/*.import.scss';
</style>
